"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Ticket } from "@/lib/types/database";
import { useTicketEditMode } from "@/contexts/TicketEditModeContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SafeEmailBody } from "@/components/crm/messages/components/SafeEmailBody";
import { ClampedText } from "@/components/shared/ClampedText";
import { cleanMessageBody, isHtmlContent } from "@/lib/email/safe-email-body-utils";
import { useHydratedTicketDescription } from "@/lib/hooks/use-hydrated-ticket-description";

interface TicketDescriptionProps {
  ticket: Ticket & {
    description_preview?: string | null;
    description_s3_key?: string | null;
  };
}

export function TicketDescription({ ticket }: TicketDescriptionProps) {
  const editMode = useTicketEditMode();
  const {
    description: hydratedDescription,
    loading: hydrating,
    error: hydrateError,
  } = useHydratedTicketDescription({
    ticketId: ticket.id,
    inlineDescription: ticket.description,
    descriptionS3Key: ticket.description_s3_key,
    previewSnippet: ticket.description_preview,
  });

  const [localValue, setLocalValue] = useState<string>(() =>
    typeof hydratedDescription === "string" ? hydratedDescription : ""
  );

  useEffect(() => {
    const next =
      typeof hydratedDescription === "string" ? hydratedDescription : "";
    setLocalValue(next);
  }, [hydratedDescription]);

  const currentDescription =
    typeof hydratedDescription === "string" ? hydratedDescription : "";
  const cleanedDescription = useMemo(
    () => cleanMessageBody(currentDescription),
    [currentDescription],
  );
  const isEmailDescription = useMemo(() => {
    if (!cleanedDescription) return false;

    if (isHtmlContent(cleanedDescription)) return true;

    // Handle plain-text forwarded/replied email payloads that include header scaffolding.
    const hasEmailHeaders =
      /^from:\s.+/im.test(cleanedDescription) &&
      /^subject:\s.+/im.test(cleanedDescription);

    return hasEmailHeaders;
  }, [cleanedDescription]);
  const hasDirty =
    editMode?.isEditMode &&
    localValue.trim() !== currentDescription.trim();

  const handleConfirm = useCallback(() => {
    if (!editMode?.isEditMode) return;
    editMode.onUpdate({ description: localValue.trim() || null });
  }, [editMode, localValue]);

  const handleCancel = useCallback(() => {
    setLocalValue(currentDescription);
  }, [currentDescription]);

  const isEditMode = editMode?.isEditMode ?? false;

  return (
    <div className="space-y-3">
      <h4 className="text-md font-semibold text-card-foreground">Description</h4>
      <div className="bg-card-50 rounded-lg p-4 space-y-3">
        {isEditMode ? (
          <>
            <Textarea
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              placeholder="No description provided."
              className="min-h-[120px] text-sm text-gray-700 dark:text-gray-300 resize-y"
              rows={5}
              disabled={hydrating}
            />
            {hasDirty && (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleConfirm}>
                  Confirm
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            {hydrating && !cleanedDescription ? (
              <div className="text-sm text-muted-foreground">
                Loading description…
              </div>
            ) : hydrateError && !cleanedDescription ? (
              <div className="text-sm text-destructive">{hydrateError}</div>
            ) : !cleanedDescription ? (
              <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                No description provided.
              </div>
            ) : isEmailDescription ? (
              <SafeEmailBody body={cleanedDescription} variant="full" collapseQuotedReplies className="text-gray-700 dark:text-gray-300" />
            ) : (
              <ClampedText
                text={cleanedDescription}
                lines={6}
                className="text-sm text-gray-700 dark:text-gray-300"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
